import React from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';

import { Box, FormGroup, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { patientCheckInviteToken, patientRegistration, setPassword } from '@uplift/api';
import { PASSWORD_MATCHES } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n, useStore } from '@uplift/hooks';
import { l10nEnService } from '@uplift/services';
import { isValidEmail } from '@uplift/validations';
import { CheckboxWithLabel, ExternalLink, PasswordField, TextInput, useAlert } from '@uplift/ui-v2';

import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components/step-container';
import { CreateAccountNextData, MatchingFlowTypeEnum } from '../types';
import { getUtmParamsFromCookie } from '../../utils';


export type CreateAccountProps = {
  onNext: (data: CreateAccountNextData) => void;
  onBack?: () => void;
  matchingFlow: MatchingFlowTypeEnum;
  referrer?: string;
  inviteToken: string;
  banner?: React.ReactNode;
  matchId?: string;
  providerId: string;
};

type CreateAccountFormType = {
  email: string;
  password: string;
};

export const CreateAccount: React.VFC<CreateAccountProps> = (props) => {
  const { classes } = useCreateAccountStyles();
  const currentUser = useCurrentuser();
  const { authStore } = useStore();
  const l10n = useL10n();
  const alert = useAlert();
  const validationSchema = React.useMemo(() => createValidationSchema(l10n), [ l10n ]);

  const { data: invitation, isInitialLoading: invitationLoading } = useQuery({
    queryKey: [ 'patientCheckInviteToken', props.inviteToken ],
    queryFn: () => patientCheckInviteToken(props.inviteToken),
    enabled: props.matchingFlow === MatchingFlowTypeEnum.Invite && !!props.inviteToken,
  });

  const onSubmit = async (values: CreateAccountFormType, formikHelpers: FormikHelpers<CreateAccountFormType>) => {

    const utmParams = getUtmParamsFromCookie();

    if (props.matchingFlow === MatchingFlowTypeEnum.Invite) {
      if (!invitation) {
        return;
      }

      const result = await setPassword({
        token: props.inviteToken,
        password: values.password,
        isInviteToken: true,
      });

      if ('error' in result) {
        if (result.error === 'account_is_created') {
          alert.error('Account already exists. Please login.');
          return;
        }

        alert.error('Invalid invitation token');
        return;
      }

      authStore.setTokens({
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
      });
      authStore.setTokensInLocalStorage();

      return props.onNext({ matchId: invitation.matchId, isLoggedIn: true, userId: invitation.patient.id });
    } else {
      const result = await patientRegistration({
        email: values.email,
        password: values.password,
        first_name: '',
        last_name: '',
        preferred_first_name: '',
        referrer: props.referrer ?? utmParams?.referrer,
        provider_id: props.matchingFlow === MatchingFlowTypeEnum.Direct ? props.providerId : undefined,
        utm_source: utmParams?.utm_source,
        utm_id: utmParams?.utm_id,
        utm_params: utmParams,
      });

      if ('error' in result) {
        if (result.status === 'email_error') {
          formikHelpers.setFieldError('email', l10n.validations.emailTaken);
          return;
        }
      }

      if ('accessToken' in result) {
        authStore.setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        authStore.setTokensInLocalStorage();
      }

      if ('matchId' in result) {
        props.onNext({ matchId: result.matchId, isLoggedIn: true, userId: result.userId });
      }
    }
  };

  if (invitationLoading) {
    return null;
  }

  if (authStore.isLoggedIn) {
    return (
      <StepContainer>
        {props.banner}
        <StepContent className={classes.accountCreatedContent}>
          <CheckCircle className={classes.checkCircleIcon} />
          <StepHeading>Password set for {currentUser.email}</StepHeading>
          <Typography variant="body1">
            Need to change? You can reset your password next time you login after completing booking.
          </Typography>
          <StepNextButton className={classes.accountCreatedNextButton} onClick={() => props.onNext({ matchId: props.matchId, isLoggedIn: authStore.isLoggedIn, userId: currentUser.id })} />
        </StepContent>
        <StepNavigator onBack={props.onBack} />
      </StepContainer>
    );
  }

  return (
    <Formik
      initialValues={{
        email: invitation?.patient.email ?? '',
        password: '',
        privacyPolicy: false,
        treatmentConsent: false,
        noticeOfPrivacyPolicy: false,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {formik => (
        <StepContainer>
          {props.banner}
          <StepContent className={classes.content}>
            <Typography variant="overline" color="textSecondary">Almost done</Typography>
            <StepHeading>{props.matchingFlow === MatchingFlowTypeEnum.Marketplace ? 'Create your account to see your matches' : 'Create your account to continue'}</StepHeading>
            <StepForm onSubmit={formik.handleSubmit} mt={4}>
              <Box display="flex" flexDirection="column" gap="1rem">
                <TextInput
                  fullWidth
                  label={l10n.onboarding.account.email}
                  type="email"
                  name="email"
                  color="secondary"
                  disabled={props.matchingFlow === MatchingFlowTypeEnum.Invite}
                />
                <PasswordField
                  fullWidth
                  label={l10n.onboarding.account.password}
                  name="password"
                  stackValidatorsOnMobile
                />

                <Box mt={1}>
                  <Typography variant="body1" component="p" className={classes.consentLabel} color="textPrimary">
                    {l10n.matching.payment.accept}
                  </Typography>
                  <FormGroup className={classes.consentFormGroup}>
                    <CheckboxWithLabel
                      showError
                      name="privacyPolicy"
                      color="secondary"
                      label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/privacy" text={l10n.general.privacy} />}
                    />
                    <CheckboxWithLabel
                      showError
                      name="treatmentConsent"
                      color="secondary"
                      label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/terms-of-use" text={l10n.general.terms} />}
                    />
                    <CheckboxWithLabel
                      showError
                      name="noticeOfPrivacyPolicy"
                      color="secondary"
                      label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/privacy/npp" text={l10n.matching.payment.privacy} />}
                    />
                  </FormGroup>
                </Box>
              </Box>
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton>Create Account</StepNextButton>
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const useCreateAccountStyles = makeStyles({ name: 'CreateAccount' })(theme => ({
  content: {
    maxWidth: `calc(508px + ${theme.spacing(4)})`,
  },
  accountCreatedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'center',
    textAlign: 'center',
  },
  checkCircleIcon: {
    boxSizing: 'border-box',
    width: '60px',
    height: '60px',
    color: theme.palette.success.main,
  },
  accountCreatedNextButton: {
    marginTop: theme.spacing(3),
  },
  consentLabel: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 450,
  },
  consentFormGroup: {
    paddingLeft: theme.spacing(1.5),
  }
}));

const createValidationSchema = (l10n: typeof l10nEnService) =>
  yup.object({
    email: yup.string().email(l10n.validations.invalidEmail).test('ascii-email', l10n.validations.validEmailOnly, isValidEmail).required(l10n.validations.requiredEmail),
    password: yup.string()
      .max(125, l10n.validations.passwordMax)
      .required(l10n.validations.requiredPass)
      .matches(PASSWORD_MATCHES, l10n.validations.passwordMatches),
    privacyPolicy: yup.bool().oneOf([ true ], l10n.validations.required),
    treatmentConsent: yup.bool().oneOf([ true ], l10n.validations.required),
    noticeOfPrivacyPolicy: yup.bool().oneOf([ true ], l10n.validations.required),
  });
